import { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { css } from 'styled-components';
import tw from 'tailwind.macro';
import { formatPriceCents } from '../../helpers/results';
import { PriceHistories } from '../../services/priceHistory/types';
import { ErrorMessageTemplate } from '../ErrorMessage/messageTemplate';
import LoadingModal from '../LoadingModal';
import ComparisonBox from './box';
import BoxActions from './boxActions';
import { useCheapTicketsTab } from './cheapTicketsTab.hook';
import { CHEAP_TICKETS_TAB_AVAILABLE_OPTIONS } from './constants';
import messages from './messages';
import { Text, TextHiglight } from './text';
import { TripComparatorWidgetProps } from './types';
import { comparePriceHistories } from './utils';

const CheapTicketsTab = (props: TripComparatorWidgetProps) => {
  const { availableValues, hasPriceData, isLoading, priceHistories } =
    useCheapTicketsTab(props);

  if (isLoading) {
    return (
      <LoadingModal
        messages={<FormattedMessage {...messages.loading} />}
        option={{ isFixed: false, withBackground: false, canScroll: true }}
      />
    );
  }

  if (!hasPriceData) {
    return (
      <span>
        <ErrorMessageTemplate
          buttonContent={<FormattedMessage {...messages.launchSearch} />}
          customStyle={css`
            ${tw`mt-0`}
          `}
          onButtonClick={() => {
            window.scrollTo(0, 0);
          }}
          paragraph={
            <FormattedMessage {...messages.tripStatisticsNotAvailable} />
          }
          title={<FormattedMessage {...messages.statisticsUnavailable} />}
        />
      </span>
    );
  }

  return (
    <WidgetIntl
      availableValues={availableValues}
      priceHistories={priceHistories}
    />
  );
};

type WidgetProps = {
  priceHistories: PriceHistories;
  availableValues: string[];
  intl: any;
};

const Widget = (props: WidgetProps) => {
  const { availableValues, intl, priceHistories } = props;

  const [firstSelectValue, setFirstSelectValue] = useState<string>(
    availableValues[0],
  );
  const [secondSelectValue, setSecondSelectValue] = useState<string>(
    availableValues[1],
  );

  const generateOptions = () => {
    return CHEAP_TICKETS_TAB_AVAILABLE_OPTIONS.map((day) => ({
      children: `📆 ${day}`,
      value: day.toString(),
      isDisabled: priceHistories[day] === null,
    }));
  };

  const firstPriceHistory = priceHistories[firstSelectValue];
  const secondPriceHistory = priceHistories[secondSelectValue];

  const { absolutePriceDifference, percentageDifference, priceDifference } =
    comparePriceHistories(firstPriceHistory, secondPriceHistory);

  const handleFirstSelectChange = (value: string) => {
    if (value === secondSelectValue) {
      setSecondSelectValue(firstSelectValue);
    }
    setFirstSelectValue(value);
  };

  const handleSecondSelectChange = (value: string) => {
    if (value === firstSelectValue) {
      setFirstSelectValue(secondSelectValue);
    }
    setSecondSelectValue(value);
  };

  return (
    <div>
      <ComparisonBox
        firstSelectOnChange={handleFirstSelectChange}
        firstSelectOptions={generateOptions()}
        firstSelectValue={firstSelectValue}
        middleMessage={<FormattedMessage {...messages.daysBeforeInsteadOf} />}
        secondSelectOnChange={handleSecondSelectChange}
        secondSelectOptions={generateOptions()}
        secondSelectValue={secondSelectValue}
      />

      <Text data-testid="priceSaving">
        <FormattedMessage
          {...(priceDifference >= 0
            ? messages.saveMoneyOnTrip
            : messages.spendMoreOnTrip)}
          values={{
            amount: (
              <TextHiglight>
                {formatPriceCents(absolutePriceDifference)}
              </TextHiglight>
            ),
          }}
        />
      </Text>

      <Text>
        <FormattedMessage
          {...(priceDifference >= 0
            ? messages.saveMoneyPercentage
            : messages.spendMorePercentage)}
          values={{
            percentage: <TextHiglight>{percentageDifference}%</TextHiglight>,
          }}
        />
      </Text>

      <BoxActions
        content={<FormattedMessage {...messages.shareToStopComplaining} />}
        shareData={{
          text: intl.formatMessage(messages['moneySavingTip']),
        }}
        title={<FormattedMessage {...messages.complainingAboutPrices} />}
      />
    </div>
  );
};

const WidgetIntl = injectIntl(Widget);

export default CheapTicketsTab;
